import React, { FC, useEffect, useState, useCallback } from 'react';
import { debounce } from 'lodash';
import './SearchFilter.scss';
import TextInput from 'components/Elements/TextInput';
import { ClearItem, SearchIcon } from 'components/Elements/CustomSVGIcon';
import PropTypes from 'prop-types';
import { SearchFilterProps } from '../../types';

interface DisplayTag {
    type: string;
    value: string;
}

const SearchFilter: FC<SearchFilterProps> = ({
    statusFilter: _statusFilter,
    onClear,
    searchParams,
    updateFilters,
    currentStatusText: _currentStatusText,
    onOpenFilterModal,
    currentFilters,
    actions,
}) => {
    const [text, setText] = useState<string>('');
    const [searchTerms, setSearchTerms] = useState<string[]>([]);

    const debouncedUpdateFilters = useCallback(debounce(updateFilters, 1000), []);
    const debouncedUpdateSearchTerms = useCallback(debounce(() => {
        if (text !== undefined && text !== '') {
            setSearchTerms((prevSearchTerms = []) => [...prevSearchTerms, text]);
            setText('');
        }
    }, 1500), [text]);

    useEffect(() => {
        const searchTermsParam = searchParams.get('searchTerms');
        setSearchTerms(searchTermsParam ? searchTermsParam.split(',') : []);
    }, []);

    useEffect(() => {
        debouncedUpdateSearchTerms();
        return () => {
            debouncedUpdateSearchTerms.cancel();
        };
    }, [text, debouncedUpdateSearchTerms]);

    useEffect(() => {
        const params = {
            searchTerms,
            startDate: currentFilters.startDate || '',
            endDate: currentFilters.endDate || '',
            siteText: currentFilters.siteText || [],
            statusText: currentFilters.statusText || [],
            referenceNumber: currentFilters.referenceNumber || [],
            requesterName: currentFilters.requesterName || [],
            approverName: currentFilters.approverName || [],
            dateSubmittedStart: currentFilters.dateSubmittedStart || '',
            dateSubmittedEnd: currentFilters.dateSubmittedEnd || '',
        };
        debouncedUpdateFilters(params);
        return () => {
            debouncedUpdateFilters.cancel();
        };
    }, [searchTerms, debouncedUpdateFilters]);

    const getActiveFilterCount = () => {
        let count = 0;
        if (currentFilters.startDate) count++;
        if (currentFilters.endDate) count++;
        if (currentFilters.siteText?.length) count++;
        if (currentFilters.statusText?.length) count++;
        if (currentFilters.referenceNumber?.length) count++;
        if (currentFilters.requesterName?.length) count++;
        if (currentFilters.approverName?.length) count++;
        if (currentFilters.dateSubmittedStart) count++;
        if (currentFilters.dateSubmittedEnd) count++;
        if (currentFilters.requestType?.length) count++;
        if (currentFilters.requesterType?.length) count++;
        return count;
    };

    const getDisplayItems = () => {
        const handleRemoveItemClick = (item: DisplayTag) => {
            switch (item.type) {
            case 'text': {
                const newSearchTerms = searchTerms.filter((term) => term !== item.value);
                setSearchTerms(newSearchTerms);
                break;
            }
            default:
                break;
            }
        };

        const items: DisplayTag[] = [];
        if (searchTerms.length) {
            searchTerms.forEach((term) => {
                items.push({ type: 'text', value: term });
            });
        }

        return (
            <div className="items-list-container">
                {items.map((item) => (
                    <div key={item.value} className="selected-item">
                        <p className="label">{item.value}</p>
                        <div className="icon-wrapper" onClick={() => handleRemoveItemClick(item)}>
                            <ClearItem />
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const resetFilters = () => {
        setText('');
        setSearchTerms([]);
        onClear();
    };

    const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setText(e.target.value);
    };

    return (
        <div className="dashboard-search-container">
            <div className="filter-container">
                <TextInput leftIcon={<SearchIcon />} placeholder="Search" value={text} onChange={handleTextChange} />
                <span className="filters-button" onClick={onOpenFilterModal}>
                    Filters{getActiveFilterCount() > 0 ? ` (${getActiveFilterCount()} Applied)` : ''}
                </span>
                <span className="clear-all-link" onClick={resetFilters}>
                    Clear all
                </span>
            </div>
            <div className="filter-button-container">
                {getDisplayItems()}
                {actions}
            </div>
        </div>
    );
};

SearchFilter.defaultProps = {
    statusFilter: {},
    actions: null,
};

SearchFilter.propTypes = {
    statusFilter: PropTypes.object as PropTypes.Validator<object>,
    actions: PropTypes.node,
};

export default SearchFilter;
